export enum PeriodType {
  Overall = 'Overall',
  Today = 'Today',
  ThisWeek = 'ThisWeek',
  LastWeek = 'LastWeek',
  ThisMonth = 'ThisMonth',
  LastMonth = 'LastMonth',
}

const formatDate = (language: string, date: Date, hasDay: boolean = true) => {
  const options: Intl.DateTimeFormatOptions = {
    day: hasDay ? 'numeric' : undefined,
    month: 'short',
    year: 'numeric',
  };

  const formattedDate = date
    ? new Intl.DateTimeFormat(language, options).format(date).replace(',', '')
    : undefined;

  return formattedDate;
};

const getDateRange = (
  language: string,
  start: Date,
  end: Date | undefined | null,
) => {
  return end
    ? `${formatDate(language, start)} - ${formatDate(language, end)}`
    : `${formatDate(language, start, end === null)}`;
};

export const setPeriodList = (
  currentDate: Date,
  language: string,
  createdAt: number | undefined,
) => {
  const createdStartedAt = createdAt ? new Date(createdAt) : undefined;

  const todayStart = new Date(currentDate);
  todayStart.setHours(0, 0, 0, 0);

  const weekStart = new Date(currentDate);
  weekStart.setDate(currentDate.getDate() - currentDate.getDay());
  weekStart.setHours(0, 0, 0, 0);

  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  weekEnd.setHours(23, 59, 59, 999);

  const lastWeekStart = new Date(weekStart);
  lastWeekStart.setDate(weekStart.getDate() - 7);
  lastWeekStart.setHours(0, 0, 0, 0);

  const lastWeekEnd = new Date(weekEnd);
  lastWeekEnd.setDate(weekEnd.getDate() - 7);
  lastWeekEnd.setHours(23, 59, 59, 999);

  const thisMonthStart = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1,
  );
  thisMonthStart.setHours(0, 0, 0, 0);

  const thisMonthEnd = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1,
    0,
  );
  thisMonthEnd.setHours(23, 59, 59, 999);

  const lastMonthStart = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() - 1,
    1,
  );
  lastMonthStart.setHours(0, 0, 0, 0);

  const lastMonthEnd = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    0,
  );
  lastMonthEnd.setHours(23, 59, 59, 999);

  return [
    {
      key: PeriodType.Overall,
      name: 'Overall',
      from: undefined,
      to: undefined,
      range: createdStartedAt
        ? getDateRange(language, createdStartedAt, currentDate)
        : '',
    },
    {
      key: PeriodType.Today,
      name: 'Today',
      from: todayStart,
      to: undefined,
      range: getDateRange(language, currentDate, null),
    },
    {
      key: PeriodType.ThisWeek,
      name: 'This Week',
      from: weekStart,
      to: weekEnd,
      range: getDateRange(language, weekStart, weekEnd),
    },
    {
      key: PeriodType.LastWeek,
      name: 'Last Week',
      from: lastWeekStart,
      to: lastWeekEnd,
      range: getDateRange(language, lastWeekStart, lastWeekEnd),
    },
    {
      key: PeriodType.ThisMonth,
      name: 'This Month',
      from: thisMonthStart,
      to: thisMonthEnd,
      range: getDateRange(language, thisMonthStart, undefined),
    },
    {
      key: PeriodType.LastMonth,
      name: 'Last Month',
      from: lastMonthStart,
      to: lastMonthEnd,
      range: getDateRange(language, lastMonthStart, undefined),
    },
  ];
};

export const getPeriod = (
  currentDate: Date,
  periodType: PeriodType,
  language: string,
) => {
  const periodList = setPeriodList(currentDate, language, undefined);
  const result = periodList.find((period) => period.key === periodType);
  return result;
};

export enum PracticePeriodType {
  Overall = 'Overall',
  Today = 'Today',
  ThisWeek = 'ThisWeek',
  ThisMonth = 'ThisMonth',
  ThisQuarter = 'ThisQuarter',
  ThisHalfYear = 'ThisHalfYear',
  ThisYear = 'ThisYear',
}

export const setPracticePeriodList = (currentDate: Date) => {
  const todayStart = new Date(currentDate);
  todayStart.setHours(0, 0, 0, 0);

  const weekStart = new Date(currentDate);
  weekStart.setDate(currentDate.getDate() - currentDate.getDay());
  weekStart.setHours(0, 0, 0, 0);

  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  weekEnd.setHours(23, 59, 59, 999);

  const thisMonthStart = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1,
  );
  thisMonthStart.setHours(0, 0, 0, 0);

  const thisMonthEnd = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1,
    0,
  );
  thisMonthEnd.setHours(23, 59, 59, 999);

  const currentMonth = currentDate.getMonth();
  const quarterStartMonth = Math.floor(currentMonth / 3) * 3;
  const quarterEndMonth = quarterStartMonth + 2;

  const thisQuarterStart = new Date(
    currentDate.getFullYear(),
    quarterStartMonth,
    1,
  );
  thisQuarterStart.setHours(0, 0, 0, 0);

  const thisQuarterEnd = new Date(
    currentDate.getFullYear(),
    quarterEndMonth + 1,
    0,
  );
  thisQuarterEnd.setHours(23, 59, 59, 999);

  const halfYearStartMonth = currentMonth < 6 ? 0 : 6;

  const thisHalfYearStart = new Date(
    currentDate.getFullYear(),
    halfYearStartMonth,
    1,
  );
  thisHalfYearStart.setHours(0, 0, 0, 0);

  const thisHalfYearEnd = new Date(
    currentDate.getFullYear(),
    halfYearStartMonth + 6,
    0,
  );
  thisHalfYearEnd.setHours(23, 59, 59, 999);

  const thisYearStart = new Date(currentDate.getFullYear(), 0, 1);
  thisYearStart.setHours(0, 0, 0, 0);

  const thisYearEnd = new Date(currentDate.getFullYear(), 11, 31);
  thisYearEnd.setHours(23, 59, 59, 999);

  return [
    {
      key: PracticePeriodType.Overall,
      name: 'All Time',
      from: undefined,
      to: undefined,
    },
    {
      key: PracticePeriodType.Today,
      name: 'Today',
      from: todayStart,
      to: undefined,
    },
    {
      key: PracticePeriodType.ThisWeek,
      name: 'This Week',
      from: weekStart,
      to: weekEnd,
    },
    {
      key: PracticePeriodType.ThisMonth,
      name: 'This Month',
      from: thisMonthStart,
      to: thisMonthEnd,
    },
    {
      key: PracticePeriodType.ThisQuarter,
      name: 'This Quarter',
      from: thisQuarterStart,
      to: thisQuarterEnd,
    },
    {
      key: PracticePeriodType.ThisHalfYear,
      name: 'This Half Year',
      from: thisHalfYearStart,
      to: thisHalfYearEnd,
    },
    {
      key: PracticePeriodType.ThisYear,
      name: 'This Year',
      from: thisYearStart,
      to: thisYearEnd,
    },
  ];
};

export const getPracticePeriod = (
  currentDate: Date,
  periodType: PracticePeriodType,
) => {
  const periodList = setPracticePeriodList(currentDate);
  const result = periodList.find((period) => period.key === periodType);
  return result;
};
